import {
  SwaggerClient,
  api_common_responseGetSessionInfo,
  api_requestCard_DesiredScheduleTypeEnum,
  api_requestCard_TimePeriodEnum,
  service_DailyIdleInfo,
} from '@/init/swagger';
import { picklist, parse } from 'valibot';

export type SwaggerClientType = InstanceType<typeof SwaggerClient>;
export interface SessionInfo
  extends Omit<api_common_responseGetSessionInfo, 'code' | 'message'> {
  use_type: PORTAL_USE_TYPE | null;
  portal_staffs:
    | {
        id?: number;
        ibow_staff_id?: number;
        ibow_office_id?: number;
        portal_office_id?: number;
        portal_office_name?: string;
        display_name?: string;
        role?: number;
      }[]
    | [];
  request_users:
    | {
        id?: number;
        ibow_staff_id?: number;
        organization_id?: number;
        organization_name?: string;
        display_name?: string;
        role?: number;
      }[]
    | [];
}

export const IBowAdminOrProxyLoginAuthId = 0;

export type ScheduleDailyIdleInfo = {
  id?: service_DailyIdleInfo['id'];
  idle_time: {
    span1: number | undefined;
    span2: number | undefined;
    span3: number | undefined;
    span4: number | undefined;
  };
  target_date: service_DailyIdleInfo['target_date'];
};

/** ログインタイプ */
export const PORTAL_USE_TYPE = {
  PORTAL_STAFF: 1,
  REQUEST_USER: 2,
} as const;
export type PORTAL_USE_TYPE =
  (typeof PORTAL_USE_TYPE)[keyof typeof PORTAL_USE_TYPE];
export function isPortalUseType(value: number) {
  if (value === PORTAL_USE_TYPE.PORTAL_STAFF) return true;
  if (value === PORTAL_USE_TYPE.REQUEST_USER) return true;
  return false;
}

/** ユーザー権限タイプ */
export const USER_ROLE_TYPE = {
  MEMBER: 2, // 一般権限
  ADMIN: 1, // 管理権限
  SUPER_ADMIN: 99, // 管理者ログイン用権限
} as const;
export type USER_ROLE_TYPE =
  (typeof USER_ROLE_TYPE)[keyof typeof USER_ROLE_TYPE];
export function getUserRoleStr(type: number | null | undefined) {
  switch (type) {
    case USER_ROLE_TYPE.MEMBER:
      return '一般権限';
    case USER_ROLE_TYPE.ADMIN:
      return '管理権限';
    default:
      return null;
  }
}
export const getUserRoleTypeOptions = () => {
  return Object.keys(USER_ROLE_TYPE)
    .filter((key) => key !== 'SUPER_ADMIN')
    .map((key) => ({
      value: USER_ROLE_TYPE[key as keyof typeof USER_ROLE_TYPE],
      text: getUserRoleStr(USER_ROLE_TYPE[key as keyof typeof USER_ROLE_TYPE]),
    }));
};

/** ewell側の性別タイプ 不明という項目があるためportalのGenderとは分けている */
/** ユーザー性別タイプ */
export const USER_SEX = {
  UNKNOWN: 0,
  MALE: 1,
  FEMALE: 2,
  OTHER: 3,
} as const;
export type USER_SEX = (typeof USER_SEX)[keyof typeof USER_SEX];
export function getUserSexStr(type: number | null) {
  switch (type) {
    case USER_SEX.UNKNOWN:
      return '不明';
    case USER_SEX.MALE:
      return '男性';
    case USER_SEX.FEMALE:
      return '女性';
    case USER_SEX.OTHER:
      return 'その他';
    default:
      return '不明';
  }
}

export const USER_GENDERS = [
  {
    value: 1,
    text: '男性',
    type: 'MALE',
  },
  {
    value: 2,
    text: '女性',
    type: 'FEMALE',
  },
  {
    value: 3,
    text: 'その他',
    type: 'OTHER',
  },
] as const;

export type USER_GENDER_TYPES = (typeof USER_GENDERS)[number]['type'];
export type USER_GENDER_VALUE_TYPES = (typeof USER_GENDERS)[number]['value'];
export type USER_GENDER_TEXT_TYPES = (typeof USER_GENDERS)[number]['text'];

export const USER_GENDER_BY_TYPE = USER_GENDERS.reduce(
  (accumulator, currentValue) => {
    accumulator[currentValue.type] = currentValue;
    return accumulator;
  },
  {} as Record<string, (typeof USER_GENDERS)[number]>,
);

export const USER_GENDER_BY_VALUE = USER_GENDERS.reduce(
  (accumulator, currentValue) => {
    accumulator[currentValue.value] = currentValue;
    return accumulator;
  },
  {} as Record<number, (typeof USER_GENDERS)[number]>,
);

/** 利用ユーザー組織タイプ */
export const ORGANIZATION_TYPE = {
  NURSING_STATION: 1, // 訪問看護ステーション
  HOSPITAL: 3, // 病院
  CARE_GIVING_OFFICE: 2, // 介護事業所
} as const;
export type ORGANIZATION_TYPE =
  (typeof ORGANIZATION_TYPE)[keyof typeof ORGANIZATION_TYPE];
export function getOrganizationTypeStr(type: ORGANIZATION_TYPE) {
  switch (type) {
    case ORGANIZATION_TYPE.NURSING_STATION:
      return '訪問看護ステーション';
    case ORGANIZATION_TYPE.HOSPITAL:
      return '病院';
    case ORGANIZATION_TYPE.CARE_GIVING_OFFICE:
      return '介護事業所';
    default:
      return null;
  }
}

/** 事業所タイプ */
export const PORTAL_OFFICE_TYPE = {
  /** 訪問看護ステーション */
  NURSING_STATION: 1,
  /** 医療機関 */
  HOSPITAL: 3,
  /**
   * 介護事業所
   *
   * NOTE: 現在は未使用
   */
  CARE_GIVING_OFFICE: 2,
} as const;
export type PORTAL_OFFICE_TYPE =
  (typeof PORTAL_OFFICE_TYPE)[keyof typeof PORTAL_OFFICE_TYPE];
export function getPortalOfficeTypeStr(type: PORTAL_OFFICE_TYPE) {
  switch (type) {
    case PORTAL_OFFICE_TYPE.NURSING_STATION:
      return '訪問看護ステーション';
    case PORTAL_OFFICE_TYPE.HOSPITAL:
      return '医療機関';
    default:
      return null;
  }
}

export const asPortalOfficeType = (value: number) => {
  return parse(picklist(Object.values(PORTAL_OFFICE_TYPE)), value);
};

/** 掲載検索フィルタータイプ */
/** FYI: https://ewell.sharepoint.com/:x:/r/sites/msteams_4d075a/Shared%20Documents/General/iBow%E3%83%9B%E3%82%9A%E3%83%BC%E3%82%BF%E3%83%AB%E3%83%95%E3%82%9A%E3%83%AD%E3%82%B7%E3%82%99%E3%82%A7%E3%82%AF%E3%83%88%E7%AE%A1%E7%90%86%E3%82%B7%E3%83%BC%E3%83%88.xlsx?d=w61de2a4a74bd415f9f223310214b5112&csf=1&web=1&e=w7tFiA&nav=MTJfRTJfezU5RERENzFBLTk5Q0ItNDlCNS04RDEyLUIyQTdEQUVFRjFEQn0 */
export const LISTING_FILTER_CATEGORY_TYPE = {
  SERVICE: 1, // サービス
  MEDICAL_CARE: 2, // 施術
  CERTIFICATION: 3, // 保有資格
  ADDITION_STATUS: 4, // 加算状況
  APPEAL_POINTS: 5, // アピールポイント
  OTHERS: 7, // その他
} as const;
export type LISTING_FILTER_CATEGORY_TYPE =
  (typeof LISTING_FILTER_CATEGORY_TYPE)[keyof typeof LISTING_FILTER_CATEGORY_TYPE];
export function getFilterCategoryTypeStr(type: LISTING_FILTER_CATEGORY_TYPE) {
  switch (type) {
    case LISTING_FILTER_CATEGORY_TYPE.SERVICE:
      return 'サービス';
    case LISTING_FILTER_CATEGORY_TYPE.MEDICAL_CARE:
      return '施術';
    case LISTING_FILTER_CATEGORY_TYPE.CERTIFICATION:
      return '保有資格';
    case LISTING_FILTER_CATEGORY_TYPE.ADDITION_STATUS:
      return '加算状況';
    case LISTING_FILTER_CATEGORY_TYPE.APPEAL_POINTS:
      return 'アピールポイント';
    case LISTING_FILTER_CATEGORY_TYPE.OTHERS:
      return 'その他';
    default:
      return null;
  }
}

export const SCHEDULE_TIME = {
  SPAN1: 'span1',
  SPAN2: 'span2',
  SPAN3: 'span3',
  SPAN4: 'span4',
} as const;
export type SCHEDULE_TIME = (typeof SCHEDULE_TIME)[keyof typeof SCHEDULE_TIME];
export function getScheduleHoursStr(value: SCHEDULE_TIME) {
  switch (value) {
    case SCHEDULE_TIME.SPAN1:
      return '9:00〜11:00';
    case SCHEDULE_TIME.SPAN2:
      return '11:00〜14:00';
    case SCHEDULE_TIME.SPAN3:
      return '14:00〜16:00';
    case SCHEDULE_TIME.SPAN4:
      return '16:00〜18:00';
  }
}

/**
 * memo: 初期リリースでは3ヶ月間で固定にする
 */
export const DEFAULT_PUBLISH_TERM = 3;
export const DEFAULT_EDIRTABLE_CALENDAR_TERM = 6;
export const CALENDAR_IDLE_STATUS = {
  EMPTY: 0, // 空きあり
  PARTIAL: 1, // 一部空きあり
  FULL: 2, // 空きなし
} as const;
export type CALENDAR_IDLE_STATUS =
  (typeof CALENDAR_IDLE_STATUS)[keyof typeof CALENDAR_IDLE_STATUS];

export const CALENDAR_IDLE_STATUS_COLOR = {
  EMPTY: '#56CB02', // 空きあり
  PARTIAL: '#FFB300', // 一部空きあり
  FULL: '#595960', // 空きなし
} as const;
export type CALENDAR_IDLE_STATUS_COLOR =
  (typeof CALENDAR_IDLE_STATUS_COLOR)[keyof typeof CALENDAR_IDLE_STATUS_COLOR];

export const APPROVAL_STATUS = {
  UNPROCESSED: 0, // 未処理
  PROCESSING: 1, // 処理中
  APPROVED: 2, // 承認済み
  RETURNED: 3, // 差し戻し
  REJECTED: 4, // 却下
};
export type APPROVAL_STATUS =
  (typeof APPROVAL_STATUS)[keyof typeof APPROVAL_STATUS];

export const LISTING_REVIEW_STATUS = {
  UNPROCESSED: 0, // 未処理
  PROCESSING: 1, // 審査中
  APPROVED: 2, // 審査完了
  RETURNED: 3, // 差し戻し
};
export type LISTING_REVIEW_STATUS =
  (typeof LISTING_REVIEW_STATUS)[keyof typeof LISTING_REVIEW_STATUS];

type Time_Period = {
  value: api_requestCard_TimePeriodEnum;
  text: string;
  start: string;
  end: string;
};

export const TIME_PERIODS: Time_Period[] = [
  {
    value: 1,
    text: '9:00〜11:00',
    start: '9:00',
    end: '11:00',
  },
  {
    value: 2,
    text: '11:00〜14:00',
    start: '11:00',
    end: '14:00',
  },
  {
    value: 3,
    text: '14:00〜16:00',
    start: '14:00',
    end: '16:00',
  },
  {
    value: 4,
    text: '16:00〜18:00',
    start: '16:00',
    end: '18:00',
  },
] as const;

export const WEEKS = {
  monday: {
    text: '月',
  },
  tuesday: {
    text: '火',
  },
  wednesday: {
    text: '水',
  },
  thursday: {
    text: '木',
  },
  friday: {
    text: '金',
  },
  saturday: {
    text: '土',
  },
  sunday: {
    text: '日',
  },
} as const;
export type WEEKS_TYPE = keyof typeof WEEKS;
export type WEEKS_TEXT_TYPE = (typeof WEEKS)[keyof typeof WEEKS]['text'];

export const TIME_PERIOD_BY_VALUE = TIME_PERIODS.reduce(
  (accumulator, currentValue) => {
    accumulator[currentValue.value] = currentValue;
    return accumulator;
  },
  {} as Record<number, (typeof TIME_PERIODS)[number]>,
);

type DesiredScheduleType = {
  value: api_requestCard_DesiredScheduleTypeEnum;
  text: string;
};

export const DESIRED_SCHEDULE_TYPES: DesiredScheduleType[] = [
  {
    value: 1,
    text: 'いずれか',
  },
  {
    value: 2,
    text: '全て',
  },
] as const;

export const DESIRED_SCHEDULE_TYPE_BY_VALUE = DESIRED_SCHEDULE_TYPES.reduce(
  (accumulator, currentValue) => {
    accumulator[currentValue.value] = currentValue;
    return accumulator;
  },
  {} as Record<number, (typeof DESIRED_SCHEDULE_TYPES)[number]>,
);

export const STATION_ADVANCED_SEARCH_TYPES = {
  ZIPCODE: 'zipcode',
  ADDRESS: 'address',
  PARTNER: 'partner',
} as const;
export type STATION_ADVANCED_SEARCH_TYPES_TYPE =
  (typeof STATION_ADVANCED_SEARCH_TYPES)[keyof typeof STATION_ADVANCED_SEARCH_TYPES];
export function getSearchTypeLabel(value: STATION_ADVANCED_SEARCH_TYPES_TYPE) {
  switch (value) {
    case STATION_ADVANCED_SEARCH_TYPES.ZIPCODE:
      return '郵便番号から検索';
    case STATION_ADVANCED_SEARCH_TYPES.ADDRESS:
      return '住所から検索';
    case STATION_ADVANCED_SEARCH_TYPES.PARTNER:
      return '地域連携先から検索';
  }
}
export const STATION_ADVANCED_SEARCH_INITIAL_ZIPCODE = '1000005';
export const HOSPITAL_ADVANCED_SEARCH_INITIAL_ZIPCODE = '1000005';

export const STATIONS_SORTS_VALUES = {
  UPDATED_AT: 'updated_at',
  CREATED_AT: 'created_at',
  NEAR: 'near',
  // THANK: 'thank',
} as const;

export const STATION_SORTS = [
  { value: STATIONS_SORTS_VALUES.UPDATED_AT, text: '更新順', order: 'DESC' },
  { value: STATIONS_SORTS_VALUES.CREATED_AT, text: '作成順', order: 'DESC' },
  { value: STATIONS_SORTS_VALUES.NEAR, text: '距離順', order: 'ASC' },
  // { value: STATIONS_SORTS_VALUES.THANK, text: 'ありがとう順' },
] as const;

export type STATION_SORT_VALUE_TYPES =
  (typeof STATIONS_SORTS_VALUES)[keyof typeof STATIONS_SORTS_VALUES];
export type STATION_SORT_TEXT_TYPES = (typeof STATION_SORTS)[number]['text'];
export type STATION_SORT_ORDER_TYPES = (typeof STATION_SORTS)[number]['order'];

export const STATION_SORT_TYPE_BY_VALUE = STATION_SORTS.reduce(
  (accumulator, currentValue) => {
    accumulator[currentValue.value] = currentValue;
    return accumulator;
  },
  {} as Record<string, (typeof STATION_SORTS)[number]>,
);

export const getOrderBySort = (sort?: string) => {
  if (!sort) {
    return 'DESC';
  }

  const sortType = STATION_SORT_TYPE_BY_VALUE[sort];
  if (!sortType) {
    return 'DESC';
  }

  return sortType.order;
};

export const APP_HOME_LIMIT = 20;
export const STATION_LIMIT = 30;
export const HOSPITAL_LIMIT = 30;

export const MOVING_MINUTES = [
  { value: 'walk_minutes_5', text: '徒歩5分' },
  { value: 'walk_minutes_10', text: '徒歩10分' },
  { value: 'walk_minutes_15', text: '徒歩15分' },
  { value: 'bicycle_minutes_10', text: '自転車10分' },
  { value: 'bicycle_minutes_20', text: '自転車20分' },
  { value: 'bicycle_minutes_30', text: '自転車30分' },
  { value: 'car_minutes_10', text: '車10分' },
  { value: 'car_minutes_20', text: '車20分' },
  { value: 'car_minutes_30', text: '車30分' },
] as const;

export type MOVING_MINUTE_VALUE_TYPES =
  (typeof MOVING_MINUTES)[number]['value'];
export type MOVING_MINUTE_TEXT_TYPES = (typeof MOVING_MINUTES)[number]['text'];

export const MOVING_MINUTE_BY_VALUE = MOVING_MINUTES.reduce(
  (accumulator, currentValue) => {
    accumulator[currentValue.value] = currentValue;
    return accumulator;
  },
  {} as Record<string, (typeof MOVING_MINUTES)[number]>,
);

export const MOVING_MINUTES_TYPE = {
  WALK_MINUTES: 'walk_minutes',
  BICYCLE_MINUTES: 'bicycle_minutes',
  CAR_MINUTES: 'car_minutes',
} as const;
export type MOVING_MINUTES_TYPE =
  (typeof MOVING_MINUTES_TYPE)[keyof typeof MOVING_MINUTES_TYPE];

export const CALENDAR_MATCH_TYPES = [
  {
    value: 0,
    text: '全てが空いている',
  },
  {
    value: 1,
    text: 'いずれかが空いている',
  },
];

export const CALENDAR_WEEKS = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
} as const;

export type CALENDAR_WEEKS_TYPE =
  (typeof CALENDAR_WEEKS)[keyof typeof CALENDAR_WEEKS];

const CALENDAR_WEEK_TEXTS: { [key in CALENDAR_WEEKS_TYPE]: string } = {
  0: '日',
  1: '月',
  2: '火',
  3: '水',
  4: '木',
  5: '金',
  6: '土',
};
export function getCalendarWeekText(weekValue: CALENDAR_WEEKS_TYPE) {
  return CALENDAR_WEEK_TEXTS[weekValue];
}

export const CALENDAR_ZONES = [
  { start: '9:00', end: '11:00' },
  { start: '11:00', end: '14:00' },
  { start: '14:00', end: '16:00' },
  { start: '16:00', end: '18:00' },
] as const;

export const IBOW_SUPPORT_TEL = '0120-49-0333';

export const SEARCH_TARGET = {
  STATION: 'station',
  HOSPITAL: 'hospital',
} as const;

export type SEARCH_TARGET_VALUES =
  (typeof SEARCH_TARGET)[keyof typeof SEARCH_TARGET];

export const HOSPITAL_SEARCH_FILTER = {
  MEDICAL_CARE_CATEGORY_ID: 2,
  APPEAL_POINT_CATEGORY_ID: 6,
} as const;

export const APPEAL_POINTS_SUB_CATEGORIES = {
  ACCESSIBILITY: { sub_category_id: 1, label: '障がいへの対応' },
  SMOKING: { sub_category_id: 2, label: '分煙' },
  MEDICAL_CARE: { sub_category_id: 3, label: '診療・対応' },
  OTHER: { sub_category_id: 99, label: 'その他' },
} as const;

export function getLabelBySubCategoryId(subCategoryId: number) {
  switch (subCategoryId) {
    case APPEAL_POINTS_SUB_CATEGORIES.ACCESSIBILITY.sub_category_id:
      return APPEAL_POINTS_SUB_CATEGORIES.ACCESSIBILITY.label;
    case APPEAL_POINTS_SUB_CATEGORIES.SMOKING.sub_category_id:
      return APPEAL_POINTS_SUB_CATEGORIES.SMOKING.label;
    case APPEAL_POINTS_SUB_CATEGORIES.MEDICAL_CARE.sub_category_id:
      return APPEAL_POINTS_SUB_CATEGORIES.MEDICAL_CARE.label;
    case APPEAL_POINTS_SUB_CATEGORIES.OTHER.sub_category_id:
      return APPEAL_POINTS_SUB_CATEGORIES.OTHER.label;
    default:
      throw new Error('Invalid sub category id');
  }
}

export enum RequestStatusEnum {
  /** なし */
  None = 0,
  /** 紹介中 */
  Introduction = 1,
  /** 紹介履歴あり */
  History = 2,
}

export const CREATE_REQUEST_TARGETS = ['station', 'hospital'] as const;
export type CREATE_REQUEST_TARGET = (typeof CREATE_REQUEST_TARGETS)[number];
